import { nativeHorizontalCarousels } from 'src/components/carouselPolicy'
/* React */
import { CSSProperties, useEffect, useMemo, useState } from 'react'

import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'

/* Swiper */
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Mousewheel, Navigation, Scrollbar } from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/scrollbar'

/* Types */
import { tProductPreview } from 'src/shared/frontendTypes/product'
import { isFilterProductsResponse } from 'src/shared/frontendTypes/productsFilter'

/* Components */
import Product, { display } from 'src/components/ProductPreview/Product'
import SliderArrows from 'src/components/SliderArrows'
import NativeHorizontalSlider, { NativeSliderSlide } from 'src/components/NativeHorizontalSlider'

/* Functions */
import { post } from 'src/functions/fetch'
import { useLiveProductCommerceList } from 'src/hooks/useLiveProductCommerce'

/* Options */
import config from 'src/config'

SwiperCore.use([Navigation, Mousewheel, Scrollbar])

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(12, 1fr)',
      // Product cards span one track below 768px, including the special layouts.
      '@media (max-width: 767px)': {
        gridTemplateColumns: 'repeat(var(--landing-product-columns-mobile, 1), minmax(0, 1fr))',
      },
      gap: theme.spacing(4),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      '& a': {
        color: 'inherit',
        textDecoration: 'inherit',
        '& .MuiCardContent-root': {
          paddingBottom: theme.spacing(1),
        },
      },
    },

    variableGridColumns: {
      gridTemplateColumns: 'repeat(var(--landing-product-columns-mobile, 1), minmax(0, 1fr))',
      '& > section': {
        gridColumn: 'auto',
      },
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: 'repeat(var(--landing-product-columns, 3), minmax(0, 1fr))',
      },
    },

    highlightGrid: {
      gap: theme.spacing(5),
      [theme.breakpoints.up('md')]: {
        '& > section': {
          gridColumn: 'span 6',
        },
      },
    },

    compactGrid: {
      gap: theme.spacing(2.5),
      [theme.breakpoints.up('sm')]: {
        '& > section': {
          gridColumn: 'span 4',
        },
      },
      [theme.breakpoints.up('md')]: {
        '& > section': {
          gridColumn: 'span 3',
        },
      },
    },

    editorialGrid: {
      gap: theme.spacing(4.5),
      [theme.breakpoints.up('md')]: {
        '& > section': {
          gridColumn: 'span 6',
        },
      },
      [theme.breakpoints.up('lg')]: {
        '& > section': {
          gridColumn: 'span 4',
        },
      },
    },

    sliderRoot: {
      position: 'relative',
      overflow: 'hidden',
      overscrollBehaviorX: 'contain',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(3),
      '& .swiper': {
        overflow: 'hidden',
        scrollbarWidth: 'none',
        touchAction: 'pan-y pinch-zoom',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      },
      '& .swiper-wrapper': {
        alignItems: 'stretch',
        willChange: 'transform',
      },
      '& .swiper-slide': {
        backfaceVisibility: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexShrink: 0,
        height: 'auto',
        transform: 'translateZ(0)',
        width: 'var(--landing-product-card-width, 296px)',
      },
      '& .swiper-slide > section, & .swiper-slide > section > a': {
        display: 'block',
        height: '100%',
        width: '100%',
      },
      '& .swiper-button-prev, & .swiper-button-next': {
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: '1px solid rgba(15, 23, 42, 0.08)',
        backgroundColor: 'rgba(255,255,255,.9)',
        color: '#1f2933',
        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
        backdropFilter: 'blur(10px)',
        transition: 'opacity .18s ease, background-color .18s ease, transform .18s ease',
        '&:after': {
          fontSize: 12,
          fontWeight: 700,
        },
        '&:hover': {
          backgroundColor: '#ffffff',
          transform: 'translateY(-1px)',
        },
        [theme.breakpoints.down('sm')]: {
          display: 'none',
        },
      },
      '& .swiper-button-disabled': {
        opacity: .3,
      },
      [theme.breakpoints.down('sm')]: {
        '& .swiper-slide': {
          width: 'var(--landing-product-card-width-mobile, 238px)',
        },
      },
    },

    sliderLineRoot: {
      position: 'relative',
      overflow: 'hidden',
      overscrollBehaviorX: 'contain',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(3.25),
      '& .swiper': {
        overflow: 'hidden',
        paddingBottom: theme.spacing(.75),
        touchAction: 'pan-y pinch-zoom',
      },
      '& .swiper-wrapper': {
        alignItems: 'stretch',
        willChange: 'transform',
      },
      '& .swiper-slide': {
        backfaceVisibility: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexShrink: 0,
        height: 'auto',
        transform: 'translateZ(0)',
        width: 'var(--landing-product-card-width, 296px)',
      },
      '& .swiper-slide > section, & .swiper-slide > section > a': {
        display: 'block',
        height: '100%',
        width: '100%',
      },
      '& .swiper-button-prev, & .swiper-button-next': {
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: '1px solid rgba(15, 23, 42, 0.08)',
        backgroundColor: 'rgba(255,255,255,.9)',
        color: '#1f2933',
        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
        backdropFilter: 'blur(10px)',
        transition: 'opacity .18s ease, background-color .18s ease, transform .18s ease',
        '&:after': {
          fontSize: 12,
          fontWeight: 700,
        },
        '&:hover': {
          backgroundColor: '#ffffff',
          transform: 'translateY(-1px)',
        },
        [theme.breakpoints.down('sm')]: {
          display: 'none',
        },
      },
      '& .swiper-button-disabled': {
        opacity: .3,
      },
      '& .swiper-scrollbar': {
        left: 0,
        right: 0,
        bottom: 0,
        height: 3,
        borderRadius: 999,
        backgroundColor: 'rgba(15, 23, 42, 0.1)',
      },
      '& .swiper-scrollbar-drag': {
        borderRadius: 999,
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
      },
      [theme.breakpoints.down('sm')]: {
        '& .swiper-slide': {
          width: 'var(--landing-product-card-width-mobile, 238px)',
        },
      },
    },
    nativeSliderRoot: {
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      '& .swiper': {
        minWidth: 0,
        maxWidth: '100%',
        paddingBottom: 0,
        // cssMode uses the browser's scroll-snap gestures, like the categories.
        touchAction: 'pan-x pan-y pinch-zoom',
      },
      '& .swiper-wrapper': {
        overflowX: 'auto',
        overflowY: 'hidden',
        overscrollBehaviorX: 'contain',
        willChange: 'auto',
      },
      '& .swiper-slide': {
        transform: 'none',
      },
    },

  })
)

interface Props {
  keys?: string[]
  size?: number
  products?: tProductPreview[]
  display: display[]
  layout?: 'grid' | 'highlight' | 'compact' | 'editorial' | 'slider' | 'sliderLine'
  displayMode?: 'grid' | 'slider' | 'sliderLine'
  showDiscount?: boolean
  showAvailability?: boolean
  showHoverEffect?: boolean
  preferMoodImages?: boolean
  showHookText?: boolean
  showWishlist?: boolean
  imageSizePx?: number
  columnsPerRow?: number
  columnsPerRowMobile?: number
  requireStock?: boolean
  inStockOnly?: boolean
  sliderLabel?: string
}

const sliderInteractionProps = {
  cssMode: true,
  mousewheel: { forceToAxis: true, releaseOnEdges: true, sensitivity: .75, thresholdDelta: 12 },
  watchOverflow: true,
  speed: 360,
  roundLengths: true,
  slidesPerGroupAuto: true,
  simulateTouch: true,
  grabCursor: true,
  allowTouchMove: true,
  passiveListeners: true,
  touchEventsTarget: 'container',
  touchAngle: 32,
  threshold: 5,
  resistanceRatio: .2,
  touchReleaseOnEdges: true,
  longSwipes: true,
  longSwipesRatio: .18,
  longSwipesMs: 180,
  followFinger: true,
  touchStartPreventDefault: false,
  touchMoveStopPropagation: false,
  preventInteractionOnTransition: false,
  shortSwipes: true,
  touchRatio: 1,
  preventClicks: false,
  preventClicksPropagation: false,
  centeredSlides: false,
  watchSlidesProgress: false,
} as const

const Products: React.FC<Props> = ({
  display,
  keys,
  size = 12,
  products: suppliedProducts,
  layout = 'grid',
  displayMode,
  showDiscount = true,
  showAvailability = true,
  showHoverEffect = true,
  preferMoodImages = false,
  showHookText = false,
  showWishlist = true,
  imageSizePx = 250,
  columnsPerRow,
  columnsPerRowMobile,
  requireStock,
  inStockOnly = false,
  sliderLabel = 'Produkte',
}) => {
  const classes = useStyles()
  const [products, setProducts] = useState<tProductPreview[]>([])
  const suppliedProductsForRender = useMemo(() => suppliedProducts?.slice(0, size), [suppliedProducts, size])
  const hasSuppliedProducts = Boolean(suppliedProducts)
  const keysSignature = useMemo(() => (keys || []).join('|'), [keys])

  const displayExtras = (display || []).filter(entry => entry.startsWith('bg-card-'))
  const productLayout =
    layout === 'highlight' || layout === 'compact' || layout === 'editorial'
      ? layout
      : 'grid'
  const productDisplayMode =
    displayMode || (layout === 'slider' || layout === 'sliderLine' ? layout : 'grid')

  const resolvedDisplay: display[] =
    productLayout === 'compact'
      ? [
          'title',
            ...(display.includes('price') ? ['price'] : []),
            ...(display.includes('deliverytime') ? ['deliverytime'] : []),
            ...(showHookText && display.includes('hook') ? ['hook'] : []),
            'center',
          'no-card',
          ...displayExtras,
        ] as display[]
      : productLayout === 'editorial'
        ? [
            'title',
            ...(display.includes('price') ? ['price'] : []),
            ...(display.includes('attributes') ? ['attributes'] : []),
            ...(display.includes('deliverytime') ? ['deliverytime'] : []),
            ...(showHookText && display.includes('hook') ? ['hook'] : []),
            'no-card',
            ...displayExtras,
          ] as display[]
        : productLayout === 'highlight'
          ? [
              'title',
              ...(display.includes('price') ? ['price'] : []),
              ...(display.includes('attributes') ? ['attributes'] : []),
              ...(display.includes('deliverytime') ? ['deliverytime'] : []),
              ...(showHookText && display.includes('hook') ? ['hook'] : []),
              ...displayExtras,
            ] as display[]
          : display

  const loadProducts = async (signal: AbortSignal) => {
    const data = await post({
      url: 'products',
      body: {
        // Retain every curated source for later restock refreshes.
        size: inStockOnly && keys?.length ? Math.max(size, keys.length) : size,
        keys,
        ...config.shop,
      },
      signal,
    })

    if (!isFilterProductsResponse(data)) {
      if (!signal.aborted) console.error('Konnte Produkte nicht laden!')
      return
    }

    const nextProducts = data.filterProducts.products

    if (keys) nextProducts.sort((a, b) => keys.indexOf(a.key) - keys.indexOf(b.key))

    setProducts(nextProducts)
  }

  useEffect(() => {
    if (hasSuppliedProducts) return

    const controller = new AbortController()
    loadProducts(controller.signal)

    return () => controller.abort()
  }, [keysSignature, hasSuppliedProducts, size, requireStock, inStockOnly])

  const renderRequireStock = suppliedProducts ? undefined : requireStock
  const sourceProducts = inStockOnly && suppliedProducts
    ? suppliedProducts
    : suppliedProductsForRender || products
  const liveProducts = useLiveProductCommerceList(sourceProducts, inStockOnly)
  const visibleProducts = inStockOnly
    ? liveProducts.filter(product => typeof product.stock === 'number' && product.stock > 0).slice(0, size)
    : sourceProducts

  const sliderCardWidth =
    productLayout === 'highlight'
      ? Math.max(Math.min(imageSizePx + 178, 520), 360)
      : productLayout === 'editorial'
        ? Math.max(Math.min(imageSizePx + 112, 420), 310)
        : productLayout === 'compact'
          ? Math.max(Math.min(imageSizePx + 46, 282), 218)
          : Math.max(Math.min(imageSizePx + 82, 330), 248)

  const sliderMobileCardWidth =
    productLayout === 'highlight'
      ? Math.max(Math.min(imageSizePx + 86, 330), 250)
      : productLayout === 'compact'
        ? Math.max(Math.min(imageSizePx + 28, 244), 206)
        : Math.max(Math.min(imageSizePx + 46, 270), 220)

  const sliderStyle = {
    ['--landing-product-card-width' as string]: `${sliderCardWidth}px`,
    ['--landing-product-card-width-mobile' as string]: `${sliderMobileCardWidth}px`,
    ['--landing-product-stage-height' as string]: `${Math.max(Math.min(imageSizePx + (productLayout === 'highlight' ? 92 : 56), productLayout === 'highlight' ? 390 : 320), 220)}px`,
    ['--landing-product-stage-height-mobile' as string]: `${Math.max(Math.min(imageSizePx + 36, 250), 190)}px`,
    ['--landing-product-image-size' as string]: `${Math.max(Math.min(imageSizePx, productLayout === 'highlight' ? 310 : 260), 120)}px`,
    ['--landing-product-image-size-mobile' as string]: `${Math.max(Math.min(imageSizePx - 10, 220), 110)}px`,
  } as CSSProperties
  const gridColumns = Math.max(1, Math.min(6, Math.round(columnsPerRow || 3)))
  const gridColumnsMobile = Math.max(1, Math.min(3, Math.round(columnsPerRowMobile || 1)))
  const gridStyle = {
    ['--landing-product-columns' as string]: String(gridColumns),
    ['--landing-product-columns-mobile' as string]: String(gridColumnsMobile),
  } as CSSProperties

  if (productDisplayMode === 'slider' || productDisplayMode === 'sliderLine') {
    const Slide = nativeHorizontalCarousels ? NativeSliderSlide : SwiperSlide
    const slides = visibleProducts.map(product => <Slide key={product.key}>
      <Product
        product={product}
        display={resolvedDisplay}
        variant={productLayout}
        showDiscount={showDiscount}
        showAvailability={showAvailability}
        hoverEffect={showHoverEffect}
        preferMoodImages={preferMoodImages}
        showWishlist={showWishlist}
        imageSizePx={imageSizePx}
        requireStock={renderRequireStock}
        nativeHorizontalCarousel={nativeHorizontalCarousels}
      />
    </Slide>)
    return (
      <div
        className={[
          productDisplayMode === 'sliderLine' ? classes.sliderLineRoot : classes.sliderRoot,
          nativeHorizontalCarousels ? classes.nativeSliderRoot : '',
        ].join(' ')}
        style={sliderStyle}
      >
        {nativeHorizontalCarousels ? <NativeHorizontalSlider
          label={sliderLabel}
          spaceBetween={20}
          breakpoints={{ 600: { spaceBetween: 22 }, 960: { spaceBetween: 24 }, 1280: { spaceBetween: 26 } }}
        >{slides}</NativeHorizontalSlider> : <Swiper
          navigation={!nativeHorizontalCarousels}
          scrollbar={!nativeHorizontalCarousels && productDisplayMode === 'sliderLine' ? { draggable: true, dragSize: 220 } : undefined}
          slidesPerView='auto'
          spaceBetween={20}
          breakpoints={{
            600: { spaceBetween: 22 },
            960: { spaceBetween: 24 },
            1280: { spaceBetween: 26 },
          }}
          {...(nativeHorizontalCarousels ? {
            cssMode: true,
            mousewheel: { forceToAxis: true, releaseOnEdges: true },
            watchOverflow: true,
            slidesPerGroupAuto: true,
            roundLengths: true,
          } : sliderInteractionProps)}
        >
          {slides}
          {nativeHorizontalCarousels && <SliderArrows label={sliderLabel} />}
        </Swiper>}
      </div>
    )
  }

  return (
    <div
      className={[
        classes.cardGrid,
        productLayout === 'grid' ? classes.variableGridColumns : '',
        productLayout === 'highlight' ? classes.highlightGrid : '',
        productLayout === 'compact' ? classes.compactGrid : '',
        productLayout === 'editorial' ? classes.editorialGrid : '',
      ].join(' ')}
      style={gridStyle}
    >
      {visibleProducts.map(product => (
        <Product
          display={resolvedDisplay}
          key={product.key}
          product={product}
          variant={productLayout}
          showDiscount={showDiscount}
          showAvailability={showAvailability}
          hoverEffect={showHoverEffect}
          preferMoodImages={preferMoodImages}
          showWishlist={showWishlist}
          imageSizePx={imageSizePx}
          requireStock={renderRequireStock}
        />
      ))}
    </div>
  )
}

export default Products
